import { useState, useEffect, useCallback, useRef } from "react";
import { getQuote } from "../services/api";
import type { IQuoteResponse, IQuoteRequest } from "../services/api";

// Buffer de seguridad: refresh automático N segundos antes de expiración
// Esto evita que el quote expire mientras el usuario hace click en "Confirmar"
const EXPIRATION_BUFFER_SECONDS = 5;

// Umbral de advertencia: mostrar warning cuando quedan N segundos
const WARNING_THRESHOLD_SECONDS = 10;

interface UseQuoteResult {
  quote: IQuoteResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  isExpiringSoon: boolean; // true cuando queda menos de WARNING_THRESHOLD_SECONDS
  timeRemaining: number; // segundos restantes hasta expiración
}

/**
 * Hook to fetch a quote for a money transfer
 * The quote is fetched when all required parameters are provided
 * 
 * Automatically refreshes the quote before it expires (with a buffer)
 * to prevent the quote from expiring while the user is confirming.
 *
 * @param sourceCountry - Source country code (e.g., 'US')
 * @param destinationCountry - Destination country code (e.g., 'MX')
 * @param fundingMethod - Funding method ('card' | 'bank_account' | 'cash')
 * @param payoutMethod - Payout method ('card' | 'bank_account')
 * @param amount - Amount to send (null if not yet entered)
 * @param currency - Currency of the amount (e.g., 'USD')
 * @param cardId - Card ID for card payments (will be signed in the quote)
 */
export const useQuote = (
  sourceCountry: string,
  destinationCountry: string,
  fundingMethod: string | null,
  payoutMethod: string,
  amount: number | null,
  currency: string,
  cardId?: string | null
): UseQuoteResult => {
  const [quote, setQuote] = useState<IQuoteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);
  
  // Ref para evitar auto-refresh múltiples
  const isRefreshing = useRef(false);

  const fetchQuote = useCallback(async () => {
    // Don't fetch if required params are missing
    if (!fundingMethod || !amount || amount <= 0) {
      setQuote(null);
      setLoading(false);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request: IQuoteRequest = {
        sourceCountry,
        destinationCountry,
        fundingMethod,
        payoutMethod,
        amount,
        currency,
      };

      // Include cardId if provided (for card payments)
      if (cardId) {
        request.cardId = cardId;
      }

      const result = await getQuote(request);
      setQuote(result);
      
      // Calcular tiempo inicial restante
      const expiresAt = new Date(result.expiresAt).getTime();
      const now = Date.now();
      const remainingMs = expiresAt - now;
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));
      
      setTimeRemaining(remainingSeconds);
      setIsExpiringSoon(remainingSeconds <= WARNING_THRESHOLD_SECONDS);
      
      isRefreshing.current = false;
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch quote"));
      setQuote(null);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
      isRefreshing.current = false;
    } finally {
      setLoading(false);
    }
  }, [sourceCountry, destinationCountry, fundingMethod, payoutMethod, amount, currency, cardId]);

  // Debounce initial fetch
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchQuote();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [fetchQuote]);

  // Timer para actualizar timeRemaining y auto-refresh cuando expira
  useEffect(() => {
    if (!quote?.expiresAt) {
      return;
    }

    const intervalId = setInterval(() => {
      const expiresAt = new Date(quote.expiresAt).getTime();
      const now = Date.now();
      const remainingMs = expiresAt - now;
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));
      
      setTimeRemaining(remainingSeconds);
      setIsExpiringSoon(remainingSeconds <= WARNING_THRESHOLD_SECONDS);
      
      // Auto-refresh si quedan menos de EXPIRATION_BUFFER_SECONDS segundos
      // y no estamos ya refrescando
      if (remainingSeconds <= EXPIRATION_BUFFER_SECONDS && !isRefreshing.current) {
        console.log(`[useQuote] Auto-refreshing quote (${remainingSeconds}s remaining)`);
        isRefreshing.current = true;
        fetchQuote();
      }
      
      // Si el quote expiró completamente (por si el auto-refresh falló)
      if (remainingSeconds <= 0 && !isRefreshing.current) {
        console.log('[useQuote] Quote expired, forcing refresh');
        isRefreshing.current = true;
        fetchQuote();
      }
    }, 1000); // Actualizar cada segundo

    return () => clearInterval(intervalId);
  }, [quote?.expiresAt, fetchQuote]);

  return {
    quote,
    loading,
    error,
    refetch: fetchQuote,
    isExpiringSoon,
    timeRemaining,
  };
};
